import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Checkbox, TextField, Select, MenuItem, SelectChangeEvent, Autocomplete, Grid2, Box, Button, Typography, FormControl, FormHelperText } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { User } from '../types/userTypes';
// El guardado de proyectos y países lo realiza el componente padre (Users.handleEditUser).
import { Positions } from '../types/EmployeeTypes';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ColumnFilterMenu from './ColumnFilterMenu';
import { exportRowsToExcel, todayStamp } from '../utils/exportExcel';
import { rols } from '../types/rolsTypes';
import { CircularProgress } from '@mui/material';
import { ADMIN_SISTEMA } from '../contexts/constants/profiles';

interface UserTableProps {
  users: User[];
  onEditUser?: (oldRut: string, updatedUser: User) => Promise<void>;
  onDeleteUser?: (userId: string, deleteUser: User) => void;
  projects: { id: number; name: string }[];
  positions: Positions[];
  rols: rols[];
  countries?: { id: number; name: string }[];
  // Rol del usuario logueado: solo un Administrador de Sistema puede editar/eliminar a otro Admin de Sistema.
  currentUserRole?: string;
}

const UserTable: React.FC<UserTableProps> = ({ users, projects, positions, onEditUser, onDeleteUser, rols, countries = [], currentUserRole = '' }) => {
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editedUserData, setEditedUserData] = useState<User | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [user, setUser] = useState<User>();
  const [emailError, setEmailError] = useState<string>('');
  const [countryError, setCountryError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [filter, setFilter] = useState('');

  // Texto comparable: minúsculas y sin tildes, para que "Martinez" encuentre "Martínez".
  const normalize = (value: unknown) =>
    (value ?? '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // El RUT se compara sin puntos ni guion: da lo mismo escribir 12.345.678-9 o 123456789.
  const normalizeRut = (value: unknown) => normalize(value).replace(/[.\-\s]/g, '');

  // Perfil y proyectos se buscan por el texto que la tabla muestra, no por su id.
  const getProfileName = (u: User) => rols.find(profile => profile.id === u.profile)?.description || '';
  const getProjectNames = (u: User) =>
    u.projects?.map(id => projects.find(project => project.id === id)?.name).filter(Boolean).join(', ') || '';

  // Búsqueda global con lógica "O": basta que coincida uno de los criterios.
  const filteredUsers = useMemo(() => {
    const term = normalize(filter).trim();
    if (!term) return users;
    const rutTerm = normalizeRut(filter);

    return users.filter(u =>
      (rutTerm !== '' && normalizeRut(u.identificationNum).includes(rutTerm)) ||
      normalize([u.name, u.firstName, u.lastName].filter(Boolean).join(' ')).includes(term) ||
      normalize(getProfileName(u)).includes(term) ||
      normalize(u.email).includes(term) ||
      normalize(getProjectNames(u)).includes(term)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, filter, rols, projects]);

  // --- FILTROS POR COLUMNA ---
  // Segunda etapa, con lógica "Y", sobre el resultado de la búsqueda global de arriba.
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [filterMenu, setFilterMenu] = useState<{ anchorEl: null | HTMLElement; columnKey: string | null }>({
    anchorEl: null,
    columnKey: null,
  });

  /** Nombres de proyecto del usuario, uno por elemento. */
  const projectNameList = (u: User): string[] => {
    const names = (u.projects || [])
      .map(id => projects.find(project => project.id === id)?.name)
      .filter((name): name is string => !!name);
    return names.length > 0 ? names : ['Sin proyectos asociados'];
  };

  /** Nombres de país del usuario, uno por elemento. */
  const countryNameList = (u: User): string[] => {
    const names = (u.countries || [])
      .map(id => countries.find(country => country.id === id)?.name)
      .filter((name): name is string => !!name);
    return names.length > 0 ? names : ['Sin países asignados'];
  };

  /**
   * Columnas filtrables. `values` devuelve una lista porque Proyectos y Países son
   * multivaluados: la fila coincide si alguno de sus valores está seleccionado, que es
   * lo que permite aislar a todos los asignados a un proyecto o a un país.
   */
  const columnsDefinition: { id: string; label: string; values: (u: User) => string[] }[] = [
    { id: 'identificationNum', label: 'Rut / DNI', values: u => [u.identificationNum || ''] },
    { id: 'name', label: 'Nombre', values: u => [u.name || ''] },
    { id: 'firstName', label: 'Apellido Paterno', values: u => [u.firstName || ''] },
    { id: 'lastName', label: 'Apellido Materno', values: u => [u.lastName || ''] },
    { id: 'position', label: 'Cargo', values: u => [u.position || 'Sin Cargo'] },
    { id: 'profile', label: 'Perfil', values: u => [getProfileName(u) || 'Sin Perfil'] },
    { id: 'email', label: 'Correo', values: u => [u.email || ''] },
    { id: 'projects', label: 'Proyectos', values: projectNameList },
    { id: 'countries', label: 'Países', values: countryNameList },
    { id: 'description', label: 'Descripción', values: u => [u.description || ''] },
    { id: 'passwordActive', label: 'Clave Activa', values: u => [u.passwordActive === 1 ? 'Sí' : 'No'] },
  ];

  const visibleUsers = useMemo(() => filteredUsers.filter(u => (
    columnsDefinition.every(col => {
      const selected = columnFilters[col.id];
      if (!selected || selected.length === 0) return true;
      return col.values(u).some(value => selected.includes(value));
    })
  )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredUsers, columnFilters, projects, countries, rols]);

  const uniqueValues = useMemo<string[]>(() => {
    const column = columnsDefinition.find(col => col.id === filterMenu.columnKey);
    if (!column) return [];
    return Array.from(new Set(users.flatMap(column.values).filter(value => !!value))).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, projects, countries, rols, filterMenu.columnKey]);

  const handleColumnFilterChange = (selected: string[]) => {
    if (!filterMenu.columnKey) return;
    setColumnFilters(prev => ({ ...prev, [filterMenu.columnKey as string]: selected }));
  };

  const hasActiveFilters = !!filter || Object.values(columnFilters).some(values => values.length > 0);

  const clearFilters = () => {
    setFilter('');
    setColumnFilters({});
  };

  /** Exporta los usuarios tal como se están viendo, respetando búsqueda y filtros. */
  const handleExportExcel = () => {
    const rows = visibleUsers.map(u => ({
      'Rut / DNI': u.identificationNum || '',
      'Nombre': u.name || '',
      'Apellido Paterno': u.firstName || '',
      'Apellido Materno': u.lastName || '',
      'Cargo': u.position || 'Sin Cargo',
      'Perfil': getProfileName(u) || 'Sin Perfil',
      'Correo': u.email || '',
      'Proyectos': projectNameList(u).join(', '),
      'Países': countryNameList(u).join(', '),
      'Descripción': u.description || '',
      'Clave Activa': u.passwordActive === 1 ? 'Sí' : 'No',
    }));
    exportRowsToExcel(rows, 'Usuarios', `Usuarios_${todayStamp()}.xlsx`);
  };

  const handleProfileChange = (event: SelectChangeEvent<number>) => {
    const selectedProfileId = event.target.value as number;
    const selectedProfile = rols.find(profile => profile.id === selectedProfileId);
    if (editedUserData && selectedProfile) {
      setEditedUserData({
        ...editedUserData,
        profile: selectedProfile.id,
        rols: {
          id: selectedProfile.id,
          description: selectedProfile.description,
        },
      });
    }
  };

  const handleOpenConfirmModal = (id: any, user: User) => {
    setUserId(id);
    setUser(user);
    if (user !== undefined && user.firstName !== undefined && user.lastName !== undefined && user.name !== undefined) {
      setUserName(user.name + ' ' + user.firstName + ' ' + user.lastName);
      setUserId(user.identificationNum);
    } else {
      setUserName('')
    }
    setIsConfirmModalOpen(true)
  }

  const handleProjectsChange = (event: SelectChangeEvent<number[]>) => {
    if (editedUserData) {
      setEditedUserData({ ...editedUserData, projects: event.target.value as number[] });
    }
  };

  const handleCountriesChange = (event: SelectChangeEvent<number[]>) => {
    if (editedUserData) {
      setEditedUserData({ ...editedUserData, countries: event.target.value as number[] });
      setCountryError('');
    }
  };

  const handlePositionChange = (event: React.SyntheticEvent, newValue: string | null | undefined) => {
    if (editedUserData) {
      setEditedUserData({ ...editedUserData, position: newValue || '' });
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleEditClick = (user: User) => {
    setEditingUserId(user.identificationNum ?? null);
    setEditedUserData(user);
    setCountryError('');
  };

  const handleSaveClick = async () => {
    if (editedUserData && editedUserData.identificationNum !== undefined && editingUserId !== null) {

      // Prevención de error por cuenta pendiente sin configurar
      if (editedUserData.identificationNum.startsWith('PENDING-')) {
        alert("Por favor, borra el texto 'PENDING-...' y escribe el RUT real del empleado antes de asignar proyectos y guardar.");
        return;
      }

      if (!editedUserData.email || !validateEmail(editedUserData.email)) {
        setEmailError('Correo electrónico inválido');
        return;
      }

      // Guardar deja al usuario activo (el backend fuerza active = 1). Un usuario activo sin pais
      // asignado no queda restringido, sino al contrario: al no haber pais que enviar en la cabecera
      // los servicios dejan de acotar y vería la información de todos los países. El Administrador de
      // Sistema se exceptúa porque por diseño trabaja en todos.
      if (getProfileName(editedUserData) !== ADMIN_SISTEMA
          && (editedUserData.countries?.length ?? 0) === 0) {
        setCountryError('Asigna al menos un país: sin país el usuario vería la información de todos los países.');
        return;
      }

      // Limpiamos los puntos
      const rutLimpio = editedUserData.identificationNum.replace(/\./g, '');

      const transformedData = {
        ...editedUserData,
        identificationNum: rutLimpio,
        active: editedUserData.active || 1
      };

      if (onEditUser) {
        try {
          setIsSaving(true);

          // 🔹 CORRECCIÓN: Le enviamos todo el paquete al padre, él se encargará de guardar
          // al usuario, luego los proyectos, y finalmente recargar la tabla en el orden correcto.
          await onEditUser(editingUserId, transformedData);

          setEditingUserId(null);
          setEditedUserData(null);

        } catch (error) {
          console.error("Error al guardar:", error);
        } finally {
          setIsSaving(false);
        }
      }
    }
  };

  const handleDeleteClick = async (userParam: string) => {
    if (userId !== undefined && user !== undefined) {
      if (onDeleteUser) {
        onDeleteUser(userId, user);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editedUserData) {
      setEditedUserData({ ...editedUserData, [e.target.name]: e.target.value });
    }
  };

  const showActionsColumn = !!onEditUser || !!onDeleteUser;
  const isCurrentUserAdminSistema = currentUserRole === ADMIN_SISTEMA;

  // Solo un Admin de Sistema puede editar/eliminar a otro Admin de Sistema.
  const canModifyUser = (targetUser: User): boolean => {
    const targetRole = rols.find(p => p.id === targetUser.profile)?.description;
    if (targetRole === ADMIN_SISTEMA) {
      return isCurrentUserAdminSistema;
    }
    return true;
  };

  return (
    <Grid2>
      <TextField
        label="Buscar por RUT, nombre, perfil, correo o proyecto"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        fullWidth
        margin="normal"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {visibleUsers.length === users.length
            ? `${users.length} usuarios`
            : `${visibleUsers.length} de ${users.length} usuarios`}
        </Typography>
        {hasActiveFilters && (
          <Typography
            variant="body2"
            onClick={clearFilters}
            sx={{ color: '#004F86', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            Limpiar filtros
          </Typography>
        )}
      </Box>

    <TableContainer component={Paper} sx={{ minWidth: 650, maxHeight: 700, overflow: 'auto' }}>
      <Table stickyHeader >
        <TableHead>
          <TableRow>
            {columnsDefinition.map((col) => (
              <TableCell key={col.id} sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {col.label}
                  <IconButton
                    size="small"
                    title={`Filtrar por ${col.label}`}
                    onClick={(e) => setFilterMenu({ anchorEl: e.currentTarget, columnKey: col.id })}
                    sx={{ color: (columnFilters[col.id]?.length ?? 0) > 0 ? '#004F86' : 'inherit' }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            ))}

            {showActionsColumn && (
              <TableCell sx={{ color: '#004F86', fontWeight: 'bold' }}>Acción</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleUsers.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <TextField
                    name="identificationNum"
                    value={editedUserData?.identificationNum || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  user.identificationNum
                )}
              </TableCell>

              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <TextField
                    name="name"
                    value={editedUserData?.name || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  user.name
                )}
              </TableCell>

              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <TextField
                    name="firstName"
                    value={editedUserData?.firstName || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  user.firstName
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <TextField
                    name="lastName"
                    value={editedUserData?.lastName || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  user.lastName
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <Autocomplete
                    value={editedUserData?.position || ''}
                    onChange={(event, newValue) => handlePositionChange(event, newValue)}
                    options={positions.map((position) => position.description)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cargo"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                ) : (
                  user.position || 'Sin Cargo'
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <Select
                    value={editedUserData?.profile || ''}
                    onChange={handleProfileChange}
                  >
                    {rols.map((profile) => (
                      <MenuItem key={profile.id} value={profile.id}>
                        {profile.description}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  rols.find(p => p.id === user.profile)?.description || ''
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <TextField
                    name="email"
                    value={editedUserData?.email || ''}
                    onChange={handleInputChange}
                    error={Boolean(emailError)}
                    helperText={emailError}
                  />
                ) : (
                  user.email
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <Select
                    multiple
                    value={editedUserData?.projects || []}
                    onChange={handleProjectsChange}
                    renderValue={(selected) => (selected as number[])
                      .map(id => projects.find(p => p.id === id)?.name)
                      .filter(Boolean)
                      .join(', ')}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        <Checkbox
                          checked={editedUserData?.projects?.includes(project.id) || false}
                        />
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  user.projects?.map((projectId) =>
                    projects.find(project => project.id === projectId)?.name
                  ).filter(Boolean).join(', ') || 'Sin proyectos asociados'
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <FormControl error={Boolean(countryError)} fullWidth>
                    <Select
                      multiple
                      value={editedUserData?.countries || []}
                      onChange={handleCountriesChange}
                      renderValue={(selected) => (selected as number[])
                        .map(id => countries.find(c => c.id === id)?.name)
                        .filter(Boolean)
                        .join(', ')}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          <Checkbox
                            checked={editedUserData?.countries?.includes(country.id) || false}
                          />
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {countryError && <FormHelperText>{countryError}</FormHelperText>}
                  </FormControl>
                ) : (
                  user.countries?.map((countryId) =>
                    countries.find(country => country.id === countryId)?.name
                  ).filter(Boolean).join(', ') || 'Sin países asociados'
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <TextField
                    name="description"
                    value={editedUserData?.description || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  user.description
                )}
              </TableCell>
              <TableCell>
                {editingUserId === user.identificationNum ? (
                  <Checkbox
                    checked={editedUserData?.passwordActive === 1}
                    onChange={(e) => {
                      if (editedUserData) {
                        setEditedUserData({
                          ...editedUserData,
                          passwordActive: e.target.checked ? 1 : 0,
                        });
                      }
                    }}
                  />
                ) : (
                  <Checkbox checked={user.passwordActive === 1} disabled />
                )}
              </TableCell>
              {showActionsColumn && (
                <TableCell>
                  {editingUserId === user.identificationNum ? (
                    <>
                      {isSaving ? (
                        <CircularProgress size={24} sx={{ margin: '8px' }} />
                      ) : (
                        <IconButton onClick={handleSaveClick}>
                          <SaveIcon color="primary" />
                        </IconButton>
                      )}
                      <IconButton color="error" onClick={() => { setEditingUserId(null); setEditedUserData(null); }}>
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      {onEditUser && canModifyUser(user) && (
                        <IconButton onClick={() => handleEditClick(user)}>
                          <EditIcon color="primary" />
                        </IconButton>
                      )}

                      {onDeleteUser && canModifyUser(user) && (
                        <IconButton onClick={() => handleOpenConfirmModal(user.identificationNum, user)}>
                          <DeleteIcon color="primary" />
                        </IconButton>
                      )}
                    </>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmDeleteModal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}
        onSubmit={handleDeleteClick} id={userId} name={userName} title={'USER'}></ConfirmDeleteModal>
    </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleExportExcel}
        >
          Exportar Excel
        </Button>
      </Box>

      <ColumnFilterMenu
        anchorEl={filterMenu.anchorEl}
        values={uniqueValues}
        selected={filterMenu.columnKey ? (columnFilters[filterMenu.columnKey] || []) : []}
        onClose={() => setFilterMenu({ anchorEl: null, columnKey: null })}
        onChange={handleColumnFilterChange}
      />
    </Grid2>
  );
};

export default UserTable;